import { useEffect, useState } from "react";
import { Copilot } from "./Copilot";

/**
 * Floating bottom-right bubble in nomni green — Aiko's companion entry
 * on the familiar shell. Existing buyers can ignore her entirely; the
 * dashboard, orders, invoices flows work without her. Click the bubble
 * and a right-side drawer slides in with the streaming chat + render
 * surface.
 *
 * Listens for "aiko:ask" custom events from anywhere in the app (Try-this
 * buttons on page invites) and seeds the Copilot prompt accordingly.
 */
export function AikoBubble() {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState<string | undefined>();
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    function onAsk(e: Event) {
      const text = (e as CustomEvent<string>).detail;
      if (typeof text === "string" && text.length > 0) {
        setSeed(text);
        setOpen(true);
      }
    }
    window.addEventListener("aiko:ask", onAsk);
    return () => window.removeEventListener("aiko:ask", onAsk);
  }, []);

  // gentle pulse on mount so users see her once
  useEffect(() => {
    const t = setTimeout(() => setPulse(true), 1500);
    const u = setTimeout(() => setPulse(false), 4500);
    return () => { clearTimeout(t); clearTimeout(u); };
  }, []);

  return (
    <>
      <button
        type="button"
        aria-label={open ? "Close Aiko" : "Ask Aiko"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={
          "fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-nomni-green text-canvas-2 shadow-lg shadow-nomni-green/30 hover:brightness-110 transition flex items-center justify-center " +
          (pulse && !open ? "animate-pulse" : "")
        }
      >
        {open ? (
          <span aria-hidden className="text-2xl leading-none">×</span>
        ) : (
          <span aria-hidden className="text-xl leading-none">✦</span>
        )}
      </button>

      <aside
        aria-hidden={!open}
        className={
          "fixed inset-y-0 right-0 z-30 w-full sm:w-[24rem] bg-canvas-2 border-l border-line shadow-xl pad-safe transition-transform " +
          (open ? "translate-x-0" : "translate-x-full")
        }
      >
        <div className="h-full grid grid-rows-[3.5rem_1fr]">
          <header className="px-4 flex items-center justify-between border-b border-line">
            <div>
              <div className="text-base font-medium">Aiko</div>
              <div className="text-xs text-ink-mute">
                She drafts. She asks before she sends.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-ink-mute hover:text-ink text-sm"
              aria-label="Minimise"
            >
              ⌄
            </button>
          </header>
          <div className="overflow-hidden">
            <Copilot seed={seed} onSeedConsumed={() => setSeed(undefined)} />
          </div>
        </div>
      </aside>
    </>
  );
}
